import { BadgesList } from "PFComponents/badges_list/badges_list";
import { getCustomFieldValues } from "PFCore/helpers/activities";
import NoProfilesIllustration from "PFTheme/graphics/illustrations/illustration-no-profiles.svg";
import { Activity, Filters, Importance, MetaWithPagination } from "PFTypes";
import { Trans, useTranslation } from "react-i18next";

import css from "./matches_empty.module.scss";

const ValuesList = ({ values }: { values: (string | number)[] }) => (
  <div className={css.badgesList}>
    <BadgesList values={values.map((val) => ({ text: val.toString() }))} />
  </div>
);

const _getRequiredCfVals = (activity: Activity) =>
  getCustomFieldValues(activity)
    .filter(({ importance }) => importance && importance >= Importance.Essential)
    .sort((cf1, cf2) => (cf1.importance < cf2.importance ? 1 : 0))
    .map(({ value }) => value);

const _getFiltersOfType = (filters: Filters, ...typeNames: string[]) => {
  const titles: string[] = [];

  Object.values(filters).forEach((filtersGroup) => {
    Object.values(filtersGroup).find((config) => {
      if (typeNames.includes(config.type)) {
        titles.push(config.title as string);
      }
    });
  });
  return titles;
};

type EmptyMatchesTipProps = {
  values: string[];
  i18nKey?: "importance" | "range" | "date" | "location";
};

const EmptyMatchesTip = ({ values, i18nKey }: EmptyMatchesTipProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.matches.tryChanging" });

  if (values.length === 0) {
    return null;
  }

  return (
    <Trans
      i18nKey={i18nKey}
      t={t}
      values={{
        count: values.length
      }}
      components={[<ValuesList key="1" values={values} />]}
    />
  );
};

type MatchesEmptyProps = {
  activity: Activity;
  matchesMeta: MetaWithPagination;
};

export const MatchesEmpty = ({ activity, matchesMeta }: MatchesEmptyProps) => {
  const { t } = useTranslation("activities");

  const filters = (matchesMeta.filters ?? {}) as Filters;
  const anyFilters = matchesMeta.filters;
  const requiredVals = _getRequiredCfVals(activity);
  const integerFilters = _getFiltersOfType(filters, "integer");
  const dateFilters = _getFiltersOfType(filters, "before_date", "date");
  const locationFilters = _getFiltersOfType(filters, "location");
  const anyTips =
    requiredVals.length + integerFilters.length + dateFilters.length + locationFilters.length > 0;

  return (
    <div className={css.container}>
      <NoProfilesIllustration className={css.noProfilesIcon} />
      <br />
      {anyTips ? (
        <>
          <div>
            {t("show.matches.noMatchesAvailable")}
            <br />
            {t("show.matches.tryFollowingTips")}
          </div>
          <div className={css.tipsContainer}>
            <EmptyMatchesTip values={requiredVals as string[]} i18nKey="importance" />
            <EmptyMatchesTip values={integerFilters} i18nKey="range" />
            <EmptyMatchesTip values={dateFilters} i18nKey="date" />
            <EmptyMatchesTip values={locationFilters} i18nKey="location" />
          </div>
        </>
      ) : (
        <>
          {t("show.matches.noMatchesAvailable")}
          <br />
          {anyFilters && <>{`${t("show.matches.pleaseTryAddMoreAttributes")} `}</>}
        </>
      )}
    </div>
  );
};
